import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Menu, X } from "lucide-react";
import { useT } from "../../lib/i18n";
import { LanguageSwitcher } from "./LanguageSwitcher";

const Logo = ({ light = false }) => (
  <img
    src="/brand/logo-mama.png"
    alt="Mama Cucina e Pizza"
    className={`h-20 sm:h-24 w-auto transition-all ${light ? "brightness-0 invert" : ""}`}
  />
);

export const Header = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const NAV = [
    { label: t("nav.home"), href: "#home" },
    { label: t("nav.menu"), href: "#menu" },
    { label: t("nav.reviews"), href: "#recensioni" },
    { label: t("nav.asporto"), href: "#asporto" },
    { label: t("nav.contact"), href: "#contatti" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled
          ? "bg-[#F5EFE4]/90 backdrop-blur-xl border-b border-[#1E3A5D]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 sm:h-28 flex items-center justify-between">
        <button
          onClick={() => scrollTo("#home")}
          data-testid="logo-home-btn"
          className="flex items-center gap-3 group"
        >
          <Logo light={!scrolled} />
        </button>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => scrollTo(n.href)}
              data-testid={`nav-${n.href.slice(1)}-btn`}
              className="font-display text-xs tracking-[0.22em] font-semibold transition-colors text-[#1E3A5D] hover:text-[#B59410]"
            >
              {n.label}
            </button>
          ))}
          <LanguageSwitcher />
          <Button
            data-testid="nav-prenota-btn"
            onClick={() => scrollTo("#prenota")}
            className="font-display text-xs tracking-[0.22em] rounded-full bg-[#B59410] hover:bg-[#8E7308] text-white px-6 transition-transform hover:-translate-y-0.5"
          >
            {t("nav.book")}
          </Button>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="p-2 text-[#1E3A5D]"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#F5EFE4]/97 backdrop-blur-xl border-t border-[#1E3A5D]/10">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => scrollTo(n.href)}
                data-testid={`nav-mobile-${n.href.slice(1)}-btn`}
                className="text-left py-2 font-display text-xs tracking-[0.22em] text-[#1E3A5D] font-semibold"
              >
                {n.label}
              </button>
            ))}
            <Button
              data-testid="nav-mobile-prenota-btn"
              onClick={() => scrollTo("#prenota")}
              className="font-display text-xs tracking-[0.22em] rounded-full bg-[#B59410] hover:bg-[#8E7308] text-white"
            >
              {t("nav.book")}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
